import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { EquipmentEvidenceDialog } from "@/components/equipment/EquipmentDialogs";
import { ProtocolReviewDialog } from "@/components/equipment/ProtocolReviewDialog";
import {
  protocolDirectionLabel,
  protocolItemMeta,
  staffTypeLabel,
  truncateText,
} from "@/components/equipment/equipmentLabels";
import {
  canCancelProtocol,
  canRespondToProtocol,
  canReturnAsset,
  canReturnStaffItem,
  cancelEquipmentProtocol,
  EquipmentApiError,
  initiateAssetReturn,
  initiateKeyCardReturn,
  isActiveStaffEquipment,
  listEquipmentAssets,
  listEquipmentProtocols,
  listStaffEquipment,
  respondEquipmentProtocol,
} from "@/lib/equipmentApi";
import type { EquipmentAsset, EquipmentProtocol, StaffEquipmentItem } from "@/types/equipment";

interface CleanerEquipmentQueueProps {
  orgId: string;
  userId: string;
}

export function CleanerEquipmentQueue({ orgId, userId }: CleanerEquipmentQueueProps) {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [respondProtocol, setRespondProtocol] = useState<EquipmentProtocol | null>(null);
  const [returnKey, setReturnKey] = useState<StaffEquipmentItem | null>(null);
  const [returnAsset, setReturnAsset] = useState<EquipmentAsset | null>(null);

  const { data, refetch } = useQuery({
    queryKey: ["cleaner-equipment", orgId, userId],
    enabled: Boolean(orgId && userId),
    queryFn: async () => {
      const [protocols, keys, assets] = await Promise.all([
        listEquipmentProtocols({ orgId, workerId: userId }),
        listStaffEquipment(orgId, userId),
        listEquipmentAssets(orgId),
      ]);
      return {
        protocols,
        keys: keys.filter(isActiveStaffEquipment),
        assets: assets.filter((item) => item.current_holder_id === userId),
        allKeys: keys,
        allAssets: assets,
      };
    },
  });

  const run = async (action: () => Promise<unknown>, success: string) => {
    try {
      setSaving(true);
      await action();
      toast.success(success);
      await refetch();
      await queryClient.invalidateQueries({ queryKey: ["equipment-rejections", orgId] });
    } catch (error) {
      console.error("[CleanerEquipmentQueue]", error);
      toast.error(error instanceof EquipmentApiError ? error.message : "Nie udało się zapisać protokołu.");
    } finally {
      setSaving(false);
    }
  };

  const pending = (data?.protocols ?? []).filter((p) => p.status === "pending");
  const actionable = pending.filter((p) => canRespondToProtocol(p, userId, false));
  const waiting = pending.filter((p) => !canRespondToProtocol(p, userId, false));
  const respondMeta = respondProtocol
    ? protocolItemMeta(respondProtocol, data?.allAssets ?? [], data?.allKeys ?? [])
    : null;

  if (!data) return null;
  if (actionable.length === 0 && waiting.length === 0 && data.keys.length === 0 && data.assets.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sprzęt</CardTitle>
        <CardDescription>Potwierdź przekazanie albo zdaj przedmiot koordynatorowi.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {actionable.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Do akceptacji</h3>
            {actionable.map((protocol) => (
              <ProtocolRow
                key={protocol.id}
                protocol={protocol}
                meta={protocolItemMeta(protocol, data.allAssets, data.allKeys)}
                saving={saving}
                onRespond={() => setRespondProtocol(protocol)}
                onCancel={
                  canCancelProtocol(protocol, userId, false)
                    ? () => void run(() => cancelEquipmentProtocol(protocol.id), "Protokół anulowany")
                    : undefined
                }
              />
            ))}
          </div>
        )}

        {waiting.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Oczekuje na koordynatora</h3>
            {waiting.map((protocol) => (
              <ProtocolRow
                key={protocol.id}
                protocol={protocol}
                meta={protocolItemMeta(protocol, data.allAssets, data.allKeys)}
                saving={saving}
                onCancel={
                  canCancelProtocol(protocol, userId, false)
                    ? () => void run(() => cancelEquipmentProtocol(protocol.id), "Protokół anulowany")
                    : undefined
                }
              />
            ))}
          </div>
        )}

        {[...data.keys, ...data.assets].length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Twój sprzęt</h3>
            {data.keys.map((item) => (
              <div key={item.id} className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <p className="font-medium">{item.name}</p>
                  <p className="text-xs text-muted-foreground">{staffTypeLabel(item.type)}</p>
                </div>
                {canReturnStaffItem(item) && (
                  <Button size="sm" variant="outline" onClick={() => setReturnKey(item)}>
                    Zdaj
                  </Button>
                )}
              </div>
            ))}
            {data.assets.map((item) => (
              <div key={item.id} className="flex items-center justify-between gap-3 rounded-md border p-3">
                <div>
                  <p className="font-medium">{item.name}</p>
                  <p className="text-xs text-muted-foreground">
                    Nr seryjny: {item.serial_number || "—"}
                  </p>
                  <p className="text-xs text-muted-foreground">Stan: {truncateText(item.notes, 120)}</p>
                </div>
                {canReturnAsset(item) && (
                  <Button size="sm" variant="outline" onClick={() => setReturnAsset(item)}>
                    Zdaj
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <ProtocolReviewDialog
        protocol={respondProtocol}
        itemName={respondMeta?.name ?? ""}
        serialNumber={respondMeta?.serialNumber ?? null}
        saving={saving}
        onOpenChange={(open) => {
          if (!open) setRespondProtocol(null);
        }}
        onAccept={() => {
          if (!respondProtocol) return;
          void run(async () => {
            await respondEquipmentProtocol({ protocolId: respondProtocol.id, accept: true });
            setRespondProtocol(null);
          }, "Protokół zaakceptowany");
        }}
        onReject={(reason) => {
          if (!respondProtocol) return;
          void run(async () => {
            await respondEquipmentProtocol({
              protocolId: respondProtocol.id,
              accept: false,
              rejectionReason: reason,
            });
            setRespondProtocol(null);
          }, "Protokół odrzucony");
        }}
      />

      <EquipmentEvidenceDialog
        open={Boolean(returnKey)}
        title="Zdaj przedmiot"
        description="Koordynator potwierdzi odbiór."
        submitLabel="Zdaj"
        saving={saving}
        onOpenChange={(open) => {
          if (!open) setReturnKey(null);
        }}
        onSubmit={(notes, files) => {
          if (!returnKey) return;
          void run(async () => {
            await initiateKeyCardReturn({ item: returnKey, conditionNotes: notes, photos: files });
            setReturnKey(null);
          }, "Protokół zwrotu wysłany");
        }}
      />

      <EquipmentEvidenceDialog
        open={Boolean(returnAsset)}
        title="Zdaj narzędzie"
        description="Koordynator potwierdzi odbiór."
        submitLabel="Zdaj"
        saving={saving}
        initialNotes={returnAsset?.notes ?? ""}
        onOpenChange={(open) => {
          if (!open) setReturnAsset(null);
        }}
        onSubmit={(notes, files) => {
          if (!returnAsset) return;
          void run(async () => {
            await initiateAssetReturn({ asset: returnAsset, conditionNotes: notes, photos: files });
            setReturnAsset(null);
          }, "Protokół zwrotu wysłany");
        }}
      />
    </Card>
  );
}

function ProtocolRow({
  protocol,
  meta,
  saving,
  onRespond,
  onCancel,
}: {
  protocol: EquipmentProtocol;
  meta: { name: string; serialNumber: string | null; conditionNotes: string | null };
  saving: boolean;
  onRespond?: () => void;
  onCancel?: () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-md border p-3">
      <div className="space-y-1">
        <p className="font-medium">{protocolDirectionLabel(protocol.direction)}: {meta.name}</p>
        <p className="text-xs text-muted-foreground">Nr seryjny: {meta.serialNumber || "—"}</p>
        <p className="text-xs text-muted-foreground">Stan: {truncateText(meta.conditionNotes, 120)}</p>
        <Badge variant="outline">Oczekuje</Badge>
      </div>
      <div className="flex shrink-0 gap-2">
        {onRespond && (
          <Button size="sm" onClick={onRespond} disabled={saving}>
            Akceptuj
          </Button>
        )}
        {onCancel && (
          <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving}>
            Anuluj
          </Button>
        )}
      </div>
    </div>
  );
}
